"use client";

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from "react";

export interface ApiErrorDetails {
	status: number;
	message?: string;
	endpoint?: string;
	timestamp?: number;
}

interface ApiErrorContextValue {
	hasCriticalError: boolean;
	error?: ApiErrorDetails;
	reportApiError: (details: ApiErrorDetails) => void;
	clearApiError: () => void;
}

const ApiErrorContext = createContext<ApiErrorContextValue | undefined>(
	undefined
);

let externalReporter: ((details: ApiErrorDetails) => void) | null = null;

export const ApiErrorProvider = ({ children }: { children: ReactNode }) => {
	const [error, setError] = useState<ApiErrorDetails | null>(null);

	const reportApiError = useCallback((details: ApiErrorDetails) => {
		const normalized: ApiErrorDetails = {
			...details,
			timestamp: details.timestamp ?? Date.now(),
		};

		if (normalized.status >= 500) {
			setError(normalized);
		}
	}, []);

	const clearApiError = useCallback(() => setError(null), []);

	useEffect(() => {
		externalReporter = reportApiError;
		return () => {
			if (externalReporter === reportApiError) {
				externalReporter = null;
			}
		};
	}, [reportApiError]);

	const value = useMemo(
		() => ({
			hasCriticalError: Boolean(error),
			error: error ?? undefined,
			reportApiError,
			clearApiError,
		}),
		[clearApiError, error, reportApiError]
	);

	return (
		<ApiErrorContext.Provider value={value}>
			{children}
		</ApiErrorContext.Provider>
	);
};

export const useApiError = (): ApiErrorContextValue => {
	const context = useContext(ApiErrorContext);
	if (!context) {
		throw new Error("useApiError must be used within an ApiErrorProvider");
	}

	return context;
};

export const emitApiError = (details: ApiErrorDetails) => {
	if (details.status < 500) return;

	const payload: ApiErrorDetails = {
		...details,
		timestamp: details.timestamp ?? Date.now(),
	};

	externalReporter?.(payload);
};
